import { useState, useEffect } from 'react';
import { Target, ChevronRight, Clock, Play, CheckCircle, AlertTriangle, Crosshair, Zap, Brain, ShieldAlert, Plus, Loader } from 'lucide-react';
import { axios } from '../App';
import { toast } from 'sonner';

const STAGE_CONFIG = {
  detection: { label: 'Detection', icon: Target, color: 'sky', desc: 'Sensor detection & initial contact' },
  classification: { label: 'Classification', icon: Brain, color: 'purple', desc: 'AI drone identification & threat assessment' },
  mitigation_decision: { label: 'Mitigation Decision', icon: ShieldAlert, color: 'yellow', desc: 'AI countermeasure recommendation' },
  deployment: { label: 'Deployment', icon: Zap, color: 'red', desc: 'Countermeasure execution' },
};

const COLOR_MAP = {
  sky: { bg: 'bg-sky-500', ring: 'ring-sky-500', text: 'text-sky-400', bgLight: 'bg-sky-500/20', border: 'border-sky-500/40' },
  purple: { bg: 'bg-purple-500', ring: 'ring-purple-500', text: 'text-purple-400', bgLight: 'bg-purple-500/20', border: 'border-purple-500/40' },
  yellow: { bg: 'bg-yellow-500', ring: 'ring-yellow-500', text: 'text-yellow-400', bgLight: 'bg-yellow-500/20', border: 'border-yellow-500/40' },
  red: { bg: 'bg-red-500', ring: 'ring-red-500', text: 'text-red-400', bgLight: 'bg-red-500/20', border: 'border-red-500/40' },
};

function formatDuration(seconds) {
  if (seconds == null) return '--';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
}

function StageNode({ stage, config, colors, isLast }) {
  const Icon = config.icon;
  const isComplete = stage.status === 'completed';
  const isActive = stage.status === 'in_progress';
  const isPending = stage.status === 'pending';

  return (
    <div className="flex items-start gap-0" data-testid={`stage-${stage.stage}`}>
      {/* Node */}
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
          isComplete ? `${colors.bg} border-transparent` :
          isActive ? `${colors.bgLight} ${colors.border} animate-pulse` :
          'bg-zinc-800 border-zinc-700'
        }`}>
          {isComplete ? <CheckCircle size={18} className="text-white" /> :
           isActive ? <Loader size={18} className={`${colors.text} animate-spin`} /> :
           <Icon size={18} className="text-zinc-500" />}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-16 ${isComplete ? colors.bg : 'bg-zinc-700'} transition-all`} />
        )}
      </div>

      {/* Content */}
      <div className="ml-4 flex-1 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-sm font-bold uppercase tracking-wider ${isComplete ? colors.text : isPending ? 'text-zinc-500' : colors.text}`}>
            {config.label}
          </span>
          {isComplete && stage.duration_seconds != null && (
            <span className="text-[11px] font-mono text-zinc-400 flex items-center gap-1">
              <Clock size={11} /> {formatDuration(stage.duration_seconds)}
            </span>
          )}
          {stage.operator && (
            <span className="text-[10px] font-mono text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{stage.operator}</span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mb-2">{config.desc}</p>

        {/* Stage Details */}
        {Object.keys(stage.details || {}).length > 0 && isComplete && (
          <div className={`p-2.5 rounded border ${colors.border} ${colors.bgLight} space-y-1`}>
            {Object.entries(stage.details).map(([key, val]) => {
              if (val == null || val === '') return null;
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              return (
                <div key={key} className="flex items-start gap-2 text-xs">
                  <span className="text-zinc-500 min-w-[100px]">{label}:</span>
                  <span className="text-zinc-200 font-mono">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineCard({ response, isSelected, onSelect }) {
  const threatColors = {
    critical: 'border-red-500/50 bg-red-500/5', high: 'border-orange-500/50 bg-orange-500/5',
    medium: 'border-yellow-500/50 bg-yellow-500/5', low: 'border-green-500/50 bg-green-500/5'
  };
  const threatTextColors = {
    critical: 'text-red-400', high: 'text-orange-400', medium: 'text-yellow-400', low: 'text-green-400'
  };

  const completedStages = (response.stages || []).filter(s => s.status === 'completed').length;
  const totalStages = (response.stages || []).length;
  const progress = totalStages > 0 ? (completedStages / totalStages) * 100 : 0;

  return (
    <div onClick={() => onSelect(response.id)}
      className={`border rounded p-3 cursor-pointer transition-all ${
        isSelected ? 'border-sky-500 bg-sky-500/5' : `${threatColors[response.threat_level] || 'border-zinc-800'} hover:border-zinc-600`
      }`} data-testid={`response-card-${response.id}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold text-white truncate flex-1 mr-2">{response.title}</h4>
        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${
          response.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
        }`} data-testid={`status-${response.id}`}>
          {response.status}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs mb-2">
        <span className={`font-mono uppercase ${threatTextColors[response.threat_level] || 'text-zinc-400'}`}>
          {response.threat_level}
        </span>
        <span className="text-zinc-600">|</span>
        <span className="text-zinc-400">{response.domain}</span>
        {response.total_engagement_time && (
          <>
            <span className="text-zinc-600">|</span>
            <span className="text-sky-400 font-mono flex items-center gap-1">
              <Clock size={11} /> {formatDuration(response.total_engagement_time)}
            </span>
          </>
        )}
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-300 ${response.status === 'completed' ? 'bg-green-500' : 'bg-sky-500'}`}
          style={{ width: `${progress}%` }} />
      </div>
      <div className="flex items-center gap-1 mt-1.5">
        {(response.stages || []).map((s, i) => (
          <div key={i} className={`w-2 h-2 rounded-full ${
            s.status === 'completed' ? 'bg-green-500' : s.status === 'in_progress' ? 'bg-yellow-500 animate-pulse' : 'bg-zinc-700'
          }`} />
        ))}
        <span className="text-[10px] text-zinc-500 ml-1">{completedStages}/{totalStages}</span>
      </div>
    </div>
  );
}

export default function ThreatResponseTimeline() {
  const [responses, setResponses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [showSimForm, setShowSimForm] = useState(false);
  const [simForm, setSimForm] = useState({
    drone_type: '', threat_level: 'high', domain: 'air', drone_id: ''
  });

  useEffect(() => { fetchResponses(); }, []);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/threat-response');
      setResponses(res.data);
      if (res.data.length > 0 && !selectedId) setSelectedId(res.data[0].id);
    } catch { toast.error('Failed to load threat responses'); }
    finally { setLoading(false); }
  };

  const simulateKillChain = async () => {
    if (!simForm.drone_type) { toast.error('Enter drone type'); return; }
    setSimulating(true);
    try {
      const res = await axios.post('/threat-response/simulate', simForm);
      toast.success(`Kill chain simulated: ${formatDuration(res.data.total_engagement_time)} total`);
      setSelectedId(res.data.id);
      setShowSimForm(false);
      setSimForm({ drone_type: '', threat_level: 'high', domain: 'air', drone_id: '' });
      fetchResponses();
    } catch (err) {
      toast.error('Simulation failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSimulating(false); }
  };

  const selectedResponse = responses.find(r => r.id === selectedId);

  // Metrics
  const completedResponses = responses.filter(r => r.status === 'completed');
  const avgEngTime = completedResponses.length > 0
    ? completedResponses.reduce((sum, r) => sum + (r.total_engagement_time || 0), 0) / completedResponses.length
    : 0;
  const fastestTime = completedResponses.length > 0
    ? Math.min(...completedResponses.map(r => r.total_engagement_time || Infinity))
    : 0;

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="threat-response-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Crosshair size={24} className="text-sky-500" />
              Threat Response Timeline
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              Full Kill Chain Visualization | Detection → Classification → Decision → Deployment
            </p>
          </div>
          {/* Quick Metrics */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-lg font-bold font-mono text-sky-400" data-testid="total-responses">{responses.length}</div>
              <div className="text-[10px] text-zinc-500 uppercase">Responses</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold font-mono text-green-400" data-testid="avg-time">{formatDuration(avgEngTime)}</div>
              <div className="text-[10px] text-zinc-500 uppercase">Avg Time</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold font-mono text-yellow-400" data-testid="fastest-time">{formatDuration(fastestTime)}</div>
              <div className="text-[10px] text-zinc-500 uppercase">Fastest</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Response List */}
        <div className="w-[35%] border-r border-zinc-800 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Clock size={16} className="text-zinc-400" /> Kill Chain History
              </h3>
              <button onClick={() => setShowSimForm(!showSimForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase bg-sky-600/20 hover:bg-sky-600/30 text-sky-400 border border-sky-500/30 rounded transition-colors"
                data-testid="simulate-btn">
                {showSimForm ? 'Cancel' : <><Play size={12} /> Simulate</>}
              </button>
            </div>

            {showSimForm && (
              <div className="p-3 bg-zinc-900 border border-sky-500/30 rounded space-y-2 mb-3" data-testid="simulate-form">
                <h4 className="text-xs font-bold uppercase tracking-wider text-sky-400">Simulate Kill Chain</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Drone Type *</label>
                    <input type="text" value={simForm.drone_type}
                      onChange={e => setSimForm(p => ({ ...p, drone_type: e.target.value }))}
                      placeholder="e.g., FPV Kamikaze"
                      className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-sky-500"
                      data-testid="sim-drone-type" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Threat Level</label>
                    <select value={simForm.threat_level}
                      onChange={e => setSimForm(p => ({ ...p, threat_level: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white focus:outline-none focus:border-sky-500"
                      data-testid="sim-threat-level">
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Domain</label>
                    <select value={simForm.domain}
                      onChange={e => setSimForm(p => ({ ...p, domain: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white focus:outline-none focus:border-sky-500"
                      data-testid="sim-domain">
                      <option value="air">Air</option>
                      <option value="surface">Surface</option>
                      <option value="underwater">Underwater</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Drone ID (optional)</label>
                    <input type="text" value={simForm.drone_id}
                      onChange={e => setSimForm(p => ({ ...p, drone_id: e.target.value }))}
                      placeholder="e.g., UAV-001"
                      className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-sky-500"
                      data-testid="sim-drone-id" />
                  </div>
                </div>
                <button onClick={simulateKillChain} disabled={simulating}
                  className="w-full py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-800 rounded text-xs font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                  data-testid="run-simulate-btn">
                  {simulating ? <><Loader size={14} className="animate-spin" /> Running AI Kill Chain...</> : <><Play size={14} /> Run Full Simulation</>}
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loading ? (
              <div className="text-center py-8 text-zinc-500 text-sm">Loading responses...</div>
            ) : responses.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Crosshair size={36} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No threat responses yet</p>
                <p className="text-xs mt-1">Click "Simulate" to run a kill chain</p>
              </div>
            ) : (
              responses.map(r => (
                <TimelineCard key={r.id} response={r} isSelected={selectedId === r.id} onSelect={setSelectedId} />
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Kill Chain Detail */}
        <div className="w-[65%] flex flex-col overflow-hidden">
          {!selectedResponse ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <Crosshair size={48} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm">Select a threat response to view kill chain</p>
              </div>
            </div>
          ) : (
            <>
              {/* Detail Header */}
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-white" data-testid="detail-title">{selectedResponse.title}</h2>
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className={`font-mono uppercase ${
                        selectedResponse.threat_level === 'critical' ? 'text-red-400' :
                        selectedResponse.threat_level === 'high' ? 'text-orange-400' :
                        selectedResponse.threat_level === 'medium' ? 'text-yellow-400' : 'text-green-400'
                      }`}>{selectedResponse.threat_level} Threat</span>
                      <span className="text-zinc-600">|</span>
                      <span className="text-zinc-400">{selectedResponse.domain} domain</span>
                      <span className="text-zinc-600">|</span>
                      <span className="text-zinc-400">{new Date(selectedResponse.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {/* Engagement Time */}
                  {selectedResponse.total_engagement_time && (
                    <div className="text-right" data-testid="engagement-time">
                      <div className="text-2xl font-bold font-mono text-sky-400">
                        {formatDuration(selectedResponse.total_engagement_time)}
                      </div>
                      <div className="text-[10px] text-zinc-500 uppercase">Total Engagement</div>
                    </div>
                  )}
                </div>

                {/* Stage Duration Bar */}
                {selectedResponse.status === 'completed' && (
                  <div className="mt-3 flex h-3 rounded-full overflow-hidden bg-zinc-800" data-testid="duration-bar">
                    {(selectedResponse.stages || []).map((s, i) => {
                      const cfg = STAGE_CONFIG[s.stage] || {};
                      const clr = COLOR_MAP[cfg.color] || COLOR_MAP.sky;
                      const total = selectedResponse.total_engagement_time || 1;
                      const pct = ((s.duration_seconds || 0) / total) * 100;
                      return (
                        <div key={i} className={`${clr.bg} relative group`} style={{ width: `${Math.max(pct, 2)}%` }}
                          title={`${cfg.label}: ${formatDuration(s.duration_seconds)}`}>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-zinc-800 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap border border-zinc-700 z-10">
                            {cfg.label}: {formatDuration(s.duration_seconds)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Kill Chain Stages */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-xl">
                  {(selectedResponse.stages || []).map((stage, i) => {
                    const cfg = STAGE_CONFIG[stage.stage] || { label: stage.stage, icon: Target, color: 'sky', desc: '' };
                    const colors = COLOR_MAP[cfg.color] || COLOR_MAP.sky;
                    return (
                      <StageNode key={i} stage={stage} config={cfg} colors={colors}
                        isLast={i === (selectedResponse.stages || []).length - 1} />
                    );
                  })}
                </div>

                {/* After Action Summary */}
                {selectedResponse.status === 'completed' && (
                  <div className="mt-6 p-4 bg-zinc-900/80 border border-zinc-800 rounded" data-testid="after-action-summary">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-green-400 mb-3 flex items-center gap-2">
                      <CheckCircle size={14} /> After-Action Summary
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-zinc-500">Threat:</span>{' '}
                        <span className="text-zinc-200">{selectedResponse.stages?.[0]?.details?.drone_type || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Classification:</span>{' '}
                        <span className="text-zinc-200">{selectedResponse.stages?.[1]?.details?.identified_as || selectedResponse.stages?.[1]?.details?.classification || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Countermeasure:</span>{' '}
                        <span className="text-zinc-200">
                          {(selectedResponse.stages?.[3]?.details?.action || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Total Time:</span>{' '}
                        <span className="text-sky-400 font-mono">{formatDuration(selectedResponse.total_engagement_time)}</span>
                      </div>
                      {(selectedResponse.stages || []).map(s => {
                        const cfg = STAGE_CONFIG[s.stage];
                        return cfg ? (
                          <div key={s.stage}>
                            <span className="text-zinc-500">{cfg.label}:</span>{' '}
                            <span className="text-zinc-300 font-mono">{formatDuration(s.duration_seconds)}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
